import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoSearchOutline } from "react-icons/io5";
import SearchBox from "./SearchBox";
import MicButton from "../../common/ci/MicButton";
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateSearchQueryMutation, useGetTopSearchesQuery, useSearchDataMutation } from "../../Redux/Api/priceAPI";
import { useDispatch, useSelector } from "react-redux";
import { setSearch } from "../../Redux/reducer/Filter";
import { RootState } from "../../Redux/store";
import useDebounce from "../../common/hooks/useDebounce";
import { motion } from "framer-motion"

interface searchResults {
  name: string;
  _id: string;
}

const Dropdown = () => {
  const navigate = useNavigate();
  let getMainSearch = useSelector((state: RootState) => state.selection.selectedSearch);
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState<string | null>("");
  const [search, setSearchValue] = useState<string>(getMainSearch || "");
  const [searchResults, setSearchResults] = useState<searchResults[] | null>(null);
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [searchAllProducts, { data: searchResponse, isLoading: productLoading }] = useSearchDataMutation();
  const [createSearchQuery,] = useCreateSearchQueryMutation();
  const { data: queryData } = useGetTopSearchesQuery();
  const location = useLocation();
  // Memoize special option to avoid unnecessary re-renders
  const specialOption = useMemo(() => ({ value: "special", label: "All" }), []);
  const searchDebounce = useDebounce(search, 400);

  // Handle the selection of an option from the dropdown
  const handleSelectOption = useCallback((value: any) => {
    setSelectedOption(value);
  }, []);

  useEffect(() => {
    setSearchValue(getMainSearch || "");
  }, [getMainSearch]);


  const handleSearch = (value: string) => {
    setSearchValue(value);
    setShowSearchBox(true);
  };

  useEffect(() => {
    if (searchResponse) {
      setSearchResults(searchResponse);
    } else {
      setSearchResults([]);
    }
  }, [searchResponse]);


  const memoizedSearchAllProducts = useCallback(
    async (searchDebounce: string) => {
      if (!searchDebounce.trim()) {
        setSearchResults([]);
        return;
      }
      try {
        const response = await searchAllProducts(searchDebounce);
        if (!response?.data) {
          console.error("Error in search response:", response.error);
        }
      } catch (error) {
        console.error("Error searching products:", error);
      }
    },
    [searchAllProducts]
  );



  useEffect(() => {
    const fetchSearchResults = async () => {
      if (searchDebounce && searchDebounce.trim() !== "") {
        await memoizedSearchAllProducts(searchDebounce);
      } else {
        setSearchResults([]);
      }
    };
    fetchSearchResults();
  }, [searchDebounce, memoizedSearchAllProducts]);

  // Header do <Dropdown/> mount karta hai (desktop + tablet), dono hamesha DOM
  // me rehte hain — sirf CSS se chhupte hain. Pehle yahan
  // document.getElementById("search-box") aur querySelector("input[type=text]")
  // tha, jo HAMESHA pehle wale instance ko pakadta tha. Tablet width par jo
  // box dikh raha hota tha wo doosra instance hota tha, to uspar click karte hi
  // "bahar click" maan kar suggestion box turant band ho jaata tha.
  // Ab har instance sirf apna hi wrapper dekhta hai.
  const rootRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const root = rootRef.current;
      if (root && !root.contains(event.target as Node)) {
        setShowSearchBox(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);


  useEffect(() => {
    setShowSearchBox(false);
  }, [location.pathname]);

  const showSearchBoxOnClick = (event: any) => {
    if (event.key === "Enter" || event.keyCode === 13) {
      dispatch(setSearch(search));
      navigate(`/items`);
      setShowSearchBox(false);
      if (search.trim() !== "") createSearchQuery({ query: search })
    }
  };


  const closeSuggestionBox = () => {
    setShowSearchBox(false)
  }

  const onGlassClick = () => {
    if (search.trim() !== "") {
      dispatch(setSearch(search));
      navigate(`/items`);
      createSearchQuery({ query: search })
      setShowSearchBox(false);
    }
  };
  const handleTopSearchesClick = (recentSerach: string) => {
    if (recentSerach.trim() !== "") {
      dispatch(setSearch(recentSerach));
      navigate(`/items`);
      createSearchQuery({ query: recentSerach })
      setShowSearchBox(false);
    }
  };


  const handleInput = () => {
    setShowSearchBox(true);

    if (window.innerWidth < 650) {
      navigate("/searchpage");
    }
  };

  const handleInputFocus = () => {
    setShowSearchBox(true);
  };

  useEffect(() => {
    if (location.pathname !== "/items" && location.pathname !== "/subItems") {
      setSearchValue("");
      setShowSearchBox(false);
    }
  }, [location.pathname]);


  const dropdownVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.01,
      },
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.2 },
    },
  };


  return (
    <section ref={rootRef} className="hidden w-full sm:flex">
      {/* Canvas: ek hi bada search box, andar navy "Search" button. */}
      <div className="relative flex h-[46px] w-full items-center rounded-md border-2 border-ci-navy bg-white">
        {showSearchBox && (
          <motion.div id="search-box" className="absolute left-0 top-[48px] z-50 w-full"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={dropdownVariants}
          >
            <SearchBox
              searchResults={searchResults}
              isLoading={productLoading}
              close={closeSuggestionBox}
              search={search}
              onGlassClick={onGlassClick}
              queryData={queryData?.topSearches || []}
              handleTopSearchesClick={handleTopSearchesClick}
            />
          </motion.div>
        )}

        <div className="relative h-full flex-1 lg:static">
          <input
            type="text"
            aria-label="Product search"
            placeholder="TMT bar, GC sheet, 2x1 pipe — size ya brand bhi"
            value={search || ""}
            onChange={(e) => handleSearch(e.target.value)}
            onClick={handleInput}
            onFocus={handleInputFocus}
            onKeyDown={(e) => showSearchBoxOnClick(e)}
            className="h-full w-full rounded-l-[2px] px-4 text-[15px] text-ci-ink placeholder:text-ci-muted focus:outline-none"
          />
        </div>

        <MicButton
          className="mr-1"
          onResult={(text) => {
            setSearchValue(text);
            setShowSearchBox(true);
          }}
        />
        <button
          type="button"
          aria-label="Search"
          onClick={onGlassClick}
          className="flex h-full shrink-0 items-center gap-2 rounded-r-[2px] bg-ci-navy px-4 text-white transition hover:bg-ci-navy2"
        >
          <IoSearchOutline size={20} />
          <span className="hidden font-display text-[15px] font-semibold uppercase tracking-wide lg:inline">
            Search
          </span>
        </button>
      </div>
    </section>
  );
};

export default Dropdown;